export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public details?: unknown) { super(message); }
}

/** What a person says when a change moves a job past its committed date (production performance). */
export interface SlipInput { reason: string; note?: string | null; customerInformed?: boolean | null; customerDate?: string | null }
/** The prompt's answer: a reason, 'skip' (leave the jobs listed out and send the rest), or null (cancel). */
type SlipHandler = (message: string, details: unknown, canSkip: boolean) => Promise<SlipInput | 'skip' | null>;
let slipHandler: SlipHandler | null = null;
/** The app registers a dialog that asks for the reason; the request is then sent again with it. */
export function setSlipHandler(h: SlipHandler | null) { slipHandler = h; }

/** skip: a change made of several jobs' work can be sent again without the jobs listed (their references); null when nothing is left. */
interface RequestOpts { raw?: boolean; contentType?: string; slip?: SlipInput; skip?: (references: string[]) => unknown | null | Promise<unknown | null> }

async function request<T>(method: string, url: string, body?: unknown, opts: RequestOpts = {}): Promise<T> {
  const headers: Record<string, string> = { 'x-requested-with': 'emolice-mrp' };
  if (opts.slip) headers['x-commitment-slip'] = encodeURIComponent(JSON.stringify(opts.slip));
  let payload: BodyInit | undefined;
  if (body !== undefined) {
    if (typeof body === 'string' && opts.contentType) { headers['content-type'] = opts.contentType; payload = body; }
    else { headers['content-type'] = 'application/json'; payload = JSON.stringify(body); }
  }
  let res: Response;
  try {
    res = await fetch(url, { method, headers, body: payload, credentials: 'same-origin' });
  } catch {
    throw new ApiError(0, 'network', 'Cannot reach the server. Check your connection and try again.');
  }
  if (res.status === 401) {
    window.dispatchEvent(new CustomEvent('mrp:unauthenticated'));
  }
  const text = await res.text();
  if (!res.ok) {
    let err: { error?: { code?: string; message?: string; details?: unknown } } = {};
    try { err = JSON.parse(text); } catch { /* not json */ }
    // A job would move past its committed date: ask why, then send the same change again with the reason
    if (res.status === 409 && err.error?.code === 'commitment_slip' && slipHandler) {
      const slip = await slipHandler(err.error.message ?? '', err.error.details, !!opts.skip);
      if (slip === 'skip' && opts.skip) {
        const refs = ((err.error.details as { jobs?: { reference: string }[] })?.jobs ?? []).map((j) => j.reference);
        const rest = await opts.skip(refs);
        if (rest) return request<T>(method, url, rest, opts);
      } else if (slip && slip !== 'skip') return request<T>(method, url, body, { ...opts, slip });
      throw new ApiError(409, 'commitment_slip_cancelled', 'Change cancelled.');
    }
    throw new ApiError(res.status, err.error?.code ?? 'error', err.error?.message ?? `Request failed (${res.status}).`, err.error?.details);
  }
  if (opts.raw) return text as T;
  return (text ? JSON.parse(text) : undefined) as T;
}

export const api = {
  get: <T>(url: string) => request<T>('GET', url),
  post: <T>(url: string, body?: unknown, opts?: Pick<RequestOpts, 'skip'>) => request<T>('POST', url, body ?? {}, opts),
  patch: <T>(url: string, body?: unknown) => request<T>('PATCH', url, body ?? {}),
  put: <T>(url: string, body?: unknown) => request<T>('PUT', url, body ?? {}),
  del: <T>(url: string) => request<T>('DELETE', url),
  text: (url: string) => request<string>('GET', url, undefined, { raw: true }),
  postText: (url: string, body?: unknown) => request<string>('POST', url, body ?? {}, { raw: true }),
};

export function qs(params: Record<string, unknown>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '' || v === false) continue;
    if (Array.isArray(v)) { if (v.length) p.set(k, v.join(',')); continue; }
    p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}
